import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import {
  LayoutDashboard,
  Users,
  ArrowLeftRight,
  FileBarChart,
  LogOut,
  ShieldCheck,
  TrendingUp,
  Sun,
  Moon,
  Menu,
  X,
} from 'lucide-react';

export default function MainLayout() {
  const navigate = useNavigate();
  const { user, logout, isAdmin, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const closeSidebar = () => setSidebarOpen(false);

  const menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, visible: true },
    { name: 'Cari Yönetimi', path: '/cari', icon: Users, visible: true },
    { name: 'İşlemler', path: '/transactions', icon: ArrowLeftRight, visible: true },
    { name: 'Raporlar', path: '/reports', icon: FileBarChart, visible: hasPermission('view_reports') },
    { name: 'Kullanıcılar', path: '/users', icon: ShieldCheck, visible: isAdmin },
  ].filter(item => item.visible);

  const initials = user?.full_name
    ? user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : 'U';

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30 flex-shrink-0">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
          <h1 className="text-xl font-semibold tracking-widest text-slate-800 dark:text-slate-100">
            CARIO
          </h1>
        </div>
        {/* Mobilde kapat butonu */}
        <button
          onClick={closeSidebar}
          className="md:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Navigasyon */}
      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {menuItems.map((item) => (
          <NavLink
            key={item.name}
            to={item.path}
            onClick={closeSidebar}
            className={({ isActive }) =>
              `flex items-center px-3 py-2.5 rounded-lg transition-all duration-150 group text-sm ${
                isActive
                  ? 'bg-blue-600/15 text-blue-600 dark:text-blue-400 font-medium border border-blue-600/20'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 border border-transparent'
              }`
            }
          >
            <item.icon className="w-4 h-4 mr-3 flex-shrink-0 transition-transform duration-150 group-hover:scale-110" />
            {item.name}
          </NavLink>
        ))}
      </nav>

      {/* Kullanıcı & Tema & Çıkış */}
      <div className="p-3 border-t border-slate-200 dark:border-slate-800 space-y-2 flex-shrink-0">
        {/* Kullanıcı bilgisi */}
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-lg shadow-blue-600/20 text-white">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-500">{user?.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}</p>
          </div>
        </div>

        {/* Tema toggle */}
        <button
          onClick={toggleTheme}
          className="flex items-center w-full px-3 py-2.5 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-all duration-150 group text-sm border border-transparent"
        >
          {theme === 'dark' ? (
            <>
              <Sun className="w-4 h-4 mr-3 flex-shrink-0 transition-transform duration-150 group-hover:rotate-12" />
              Açık Tema
            </>
          ) : (
            <>
              <Moon className="w-4 h-4 mr-3 flex-shrink-0 transition-transform duration-150 group-hover:-rotate-12" />
              Koyu Tema
            </>
          )}
        </button>

        {/* Çıkış */}
        <button
          onClick={handleLogout}
          className="flex items-center w-full px-3 py-2.5 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-all duration-150 group text-sm border border-transparent hover:border-red-500/20"
        >
          <LogOut className="w-4 h-4 mr-3 flex-shrink-0 transition-transform duration-150 group-hover:-translate-x-0.5" />
          Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 font-sans overflow-hidden transition-colors duration-200">

      {/* Mobil overlay (sidebar açıkken) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* SIDEBAR — masaüstü: statik, mobil: slide-in drawer */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 flex flex-col flex-shrink-0
        bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
        transition-transform duration-300 ease-in-out
        md:static md:translate-x-0 md:z-auto
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <SidebarContent />
      </aside>

      {/* ANA İÇERİK */}
      <main className="flex-1 flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-950 transition-colors duration-200 min-w-0">

        {/* Mobil üst bar */}
        <div className="md:hidden h-14 flex items-center px-4 gap-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex-shrink-0 transition-colors duration-200">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-base font-semibold tracking-widest text-slate-800 dark:text-slate-100">CARIO</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
          <p className="text-center text-xs text-slate-400 dark:text-slate-600 mt-8 pb-2">
            &copy; {new Date().getFullYear()} Cario — Mustafa Sezer Başar
          </p>
        </div>
      </main>
    </div>
  );
}
